/**
 * ObjectCacheService is a class that manages a cache of objects, allowing for checking, retrieving, and updating objects by UUID.
 */
class ObjectCacheService {
    /**
     * Constructs an instance of ObjectCacheService.
     * @param {Object} $bridges - The service bridges object.
     */
    constructor($bridges) {
        this.$bridges = $bridges;
        this.$logger = $bridges.logger();
        this._logIdentifier = '$objectCache';
        this.$logger.info(this, 'initialize', this._logIdentifier);
        this._cache = {};
    }

    /**
     * Checks if an object exists in the cache.
     * @param {Object} object - The object to check.
     * @returns {boolean} - True if the object is found in the cache, otherwise false.
     */
    hasObject(object) {
        let wasFound = false,
            key, cacheObject;

        for(key in this._cache) {
            cacheObject = this._cache[key];

            if(cacheObject === object) {
                wasFound = true;
                break;
            }
        }

        return wasFound;
    }

    /**
     * Checks if an object with a specific UUID exists in the cache.
     * @param {string} uuid - The UUID of the object to check.
     * @returns {boolean} - True if an object with the UUID is found in the cache, otherwise false.
     */
    hasUUID(uuid) {
        return this._cache[uuid] !== undefined;
    }

    /**
     * Retrieves an object from the cache by its UUID.
     * @param {string} uuid - The UUID of the object to retrieve.
     * @returns {Object|undefined} - The object if found, otherwise undefined.
     */
    get(uuid) {
        return this._cache[uuid];
    }

    /**
     * Adds an object to the cache.
     * @param {Object} object - The object to add to the cache.
     * @returns {boolean} - True if the object was successfully added, otherwise false.
     */
    put(object) {
        let didSucceed = false,
            uuid;

        if(!this.hasObject(object)) {
            uuid = object.uuid || object.getUUID();

            if(uuid && typeof uuid === 'string') {
                this._cache[uuid] = object;
                didSucceed = true;
            }
        }

        return didSucceed;
    }

    /**
     * Adds a null entry for a specific UUID in the cache.
     * @param {string} uuid - The UUID to add as a null entry.
     */
    putAsNull(uuid) {
        this._cache[uuid] = null;
    }

    /**
     * Clears all entries from the cache.
     */
    clear() {
        this.$logger.dev(this, 'clearing object caches');
        this._cache = {};
    }
}

export default ObjectCacheService;